import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Check, Eye, EyeOff, Loader2, LogIn, UserPlus, X } from 'lucide-react';
import { Portal, type AuthSession } from '@kick/types';
import { loginSchema, registerCustomerSchema, usernameSchema } from '@kick/validation';
import { api, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { cn } from '@/lib/cn';

type Modo = 'entrar' | 'crear';

export function Acceso() {
  const iniciar = useAuth((s) => s.iniciar);
  const [modo, setModo] = useState<Modo>('entrar');

  return (
    <div className="min-h-dvh bg-kick-light">
      <header className="bg-kick-dark">
        <div className="mx-auto flex max-w-6xl items-center justify-center px-4 py-5">
          <a href="/">
            <img src="/logo.png" alt="Kick Domicilios" className="h-12 w-auto" />
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 py-10">
        <div className="card-kick p-6 sm:p-8">
          <div className="mb-6 flex rounded-kick bg-kick-light p-1">
            <Pestana activa={modo === 'entrar'} onClick={() => setModo('entrar')}>
              Ingresar
            </Pestana>
            <Pestana activa={modo === 'crear'} onClick={() => setModo('crear')}>
              Crear cuenta
            </Pestana>
          </div>

          {modo === 'entrar' ? (
            <FormularioIngreso onListo={iniciar} />
          ) : (
            <FormularioRegistro onListo={iniciar} />
          )}
        </div>
      </main>
    </div>
  );
}

function Pestana({
  activa,
  onClick,
  children,
}: {
  activa: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={cn(
        'flex-1 rounded-[0.6rem] px-4 py-2 text-sm font-semibold transition',
        activa ? 'bg-white text-kick-ink shadow-sm' : 'text-kick-gray hover:text-kick-ink',
      )}
    >
      {children}
    </button>
  );
}

// ── Ingreso ─────────────────────────────────────────────────

function FormularioIngreso({ onListo }: { onListo: (s: AuthSession) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [verClave, setVerClave] = useState(false);

  const entrar = useMutation({
    mutationFn: async () => {
      const datos = loginSchema.parse({ username, password, portal: Portal.SHOP });
      const { data } = await api.post<AuthSession>('/auth/login', datos);
      return data;
    },
    onSuccess: onListo,
  });

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        entrar.mutate();
      }}
      className="space-y-4"
    >
      <h1 className="text-2xl text-kick-dark">Bienvenido de vuelta</h1>

      <Campo
        id="username"
        etiqueta="Usuario"
        valor={username}
        onCambio={setUsername}
        placeholder="mgomez"
        autoComplete="username"
        autoFocus
      />

      <CampoClave
        id="password"
        etiqueta="Contraseña"
        valor={password}
        onCambio={setPassword}
        visible={verClave}
        onAlternar={() => setVerClave((v) => !v)}
        autoComplete="current-password"
      />

      {entrar.isError && <Error mensaje={getErrorMessage(entrar.error)} />}

      <button type="submit" disabled={entrar.isPending} className="btn-kick w-full">
        {entrar.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
        {entrar.isPending ? 'Ingresando…' : 'Ingresar'}
      </button>
    </form>
  );
}

// ── Registro ────────────────────────────────────────────────

function FormularioRegistro({ onListo }: { onListo: (s: AuthSession) => void }) {
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const disponibilidad = useDisponibilidadUsuario(username);

  const registrar = useMutation({
    mutationFn: async () => {
      const datos = registerCustomerSchema.parse({
        username,
        fullName,
        phone,
        password,
        confirmPassword,
        acceptedTerms: true,
      });
      const { data } = await api.post<AuthSession>('/auth/register', datos);
      return data;
    },
    onSuccess: onListo,
  });

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    setErrores({});

    const revision = registerCustomerSchema.safeParse({
      username,
      fullName,
      phone,
      password,
      confirmPassword,
      acceptedTerms: true,
    });

    if (!revision.success) {
      const encontrados: Record<string, string> = {};
      for (const issue of revision.error.issues) {
        const campo = issue.path[0];
        if (typeof campo === 'string' && !encontrados[campo]) encontrados[campo] = issue.message;
      }
      setErrores(encontrados);
      return;
    }

    registrar.mutate();
  };

  return (
    <form noValidate onSubmit={enviar} className="space-y-4">
      <h1 className="text-2xl text-kick-dark">Crea tu cuenta</h1>

      <div>
        <Campo
          id="reg-username"
          etiqueta="Usuario"
          valor={username}
          onCambio={setUsername}
          placeholder="tunombre"
          autoComplete="username"
          error={errores.username}
          autoFocus
        />
        <EstadoUsuario estado={disponibilidad} onElegir={setUsername} />
      </div>

      <Campo
        id="reg-nombre"
        etiqueta="Nombre completo"
        valor={fullName}
        onCambio={setFullName}
        placeholder="María Isabel Gómez"
        autoComplete="name"
        error={errores.fullName}
      />

      <Campo
        id="reg-tel"
        etiqueta="Celular"
        valor={phone}
        onCambio={setPhone}
        placeholder="3105551234"
        autoComplete="tel"
        inputMode="numeric"
        error={errores.phone}
      />

      <CampoClave
        id="reg-clave"
        etiqueta="Contraseña"
        valor={password}
        onCambio={setPassword}
        visible={verClave}
        onAlternar={() => setVerClave((v) => !v)}
        autoComplete="new-password"
        error={errores.password}
        ayuda="Mínimo 8 caracteres, con mayúscula, minúscula y número."
      />

      <CampoClave
        id="reg-clave2"
        etiqueta="Repite la contraseña"
        valor={confirmPassword}
        onCambio={setConfirmPassword}
        visible={verClave}
        onAlternar={() => setVerClave((v) => !v)}
        autoComplete="new-password"
        error={errores.confirmPassword}
      />

      {registrar.isError && <Error mensaje={getErrorMessage(registrar.error)} />}

      <button
        type="submit"
        disabled={registrar.isPending || disponibilidad.estado === 'ocupado'}
        className="btn-kick w-full"
      >
        {registrar.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <UserPlus className="h-5 w-5" />
        )}
        {registrar.isPending ? 'Creando cuenta…' : 'Crear cuenta'}
      </button>

      <p className="text-center text-xs text-kick-gray">
        Al crear la cuenta aceptas los términos y la política de privacidad.
      </p>
    </form>
  );
}

// ── Disponibilidad del usuario ──────────────────────────────

/**
 * Cada estado es su propio miembro de la unión: agruparlos en uno solo con
 * varios literales impide que TypeScript estreche el tipo al descartarlos.
 */
type EstadoDisponibilidad =
  | { estado: 'vacio' }
  | { estado: 'invalido' }
  | { estado: 'consultando' }
  | { estado: 'libre' }
  | { estado: 'ocupado'; sugerencias: string[] };

/** Consulta al servidor con retardo, para no disparar una petición por tecla. */
function useDisponibilidadUsuario(username: string): EstadoDisponibilidad {
  const [estado, setEstado] = useState<EstadoDisponibilidad>({ estado: 'vacio' });

  useEffect(() => {
    if (!username) {
      setEstado({ estado: 'vacio' });
      return;
    }

    if (!usernameSchema.safeParse(username).success) {
      setEstado({ estado: 'invalido' });
      return;
    }

    setEstado({ estado: 'consultando' });
    const cancelar = new AbortController();
    const temporizador = setTimeout(async () => {
      try {
        const { data } = await api.get<{ disponible: boolean; sugerencias: string[] }>(
          '/auth/username-disponible',
          { params: { username }, signal: cancelar.signal },
        );
        setEstado(
          data.disponible
            ? { estado: 'libre' }
            : { estado: 'ocupado', sugerencias: data.sugerencias },
        );
      } catch {
        // Si la consulta falla, el servidor lo validará al registrar.
        setEstado({ estado: 'vacio' });
      }
    }, 400);

    return () => {
      clearTimeout(temporizador);
      cancelar.abort();
    };
  }, [username]);

  return estado;
}

function EstadoUsuario({
  estado,
  onElegir,
}: {
  estado: EstadoDisponibilidad;
  onElegir: (u: string) => void;
}) {
  if (estado.estado === 'vacio' || estado.estado === 'invalido') return null;

  if (estado.estado === 'consultando') {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-sm text-kick-gray">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Comprobando…
      </p>
    );
  }

  if (estado.estado === 'libre') {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-sm text-emerald-600">
        <Check className="h-4 w-4" />
        Disponible
      </p>
    );
  }

  return (
    <div className="mt-1.5">
      <p className="flex items-center gap-1.5 text-sm text-red-600">
        <X className="h-4 w-4" />
        Ya está en uso
      </p>
      {estado.sugerencias.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-kick-gray">Prueba:</span>
          {estado.sugerencias.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onElegir(s)}
              className="rounded-full bg-kick-red/10 px-2.5 py-1 text-xs font-semibold text-kick-red transition hover:bg-kick-red/20"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Piezas del formulario ───────────────────────────────────

function Campo({
  id,
  etiqueta,
  valor,
  onCambio,
  error,
  ...props
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  error?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-kick-ink">
        {etiqueta}
      </label>
      <input
        id={id}
        type="text"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        aria-invalid={!!error}
        className={cn(
          'w-full rounded-kick border bg-white px-4 py-3 text-kick-ink outline-none transition',
          'focus:border-kick-red focus:ring-2 focus:ring-kick-red/20',
          error ? 'border-red-400' : 'border-kick-gray/30',
        )}
        {...props}
      />
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function CampoClave({
  id,
  etiqueta,
  valor,
  onCambio,
  visible,
  onAlternar,
  error,
  ayuda,
  ...props
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  visible: boolean;
  onAlternar: () => void;
  error?: string;
  ayuda?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-kick-ink">
        {etiqueta}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          placeholder="••••••••"
          aria-invalid={!!error}
          className={cn(
            'w-full rounded-kick border bg-white px-4 py-3 pr-12 text-kick-ink outline-none transition',
            'focus:border-kick-red focus:ring-2 focus:ring-kick-red/20',
            error ? 'border-red-400' : 'border-kick-gray/30',
          )}
          {...props}
        />
        <button
          type="button"
          onClick={onAlternar}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-kick-gray transition hover:text-kick-ink"
        >
          {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {error ? (
        <p className="mt-1.5 text-sm text-red-600">{error}</p>
      ) : (
        ayuda && <p className="mt-1.5 text-xs text-kick-gray">{ayuda}</p>
      )}
    </div>
  );
}

function Error({ mensaje }: { mensaje: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-kick border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{mensaje}</span>
    </div>
  );
}
